/* eslint-disable no-unused-vars */
/* eslint-disable no-nested-ternary */
import React, {
  forwardRef,
  useState,
  useMemo,
  useImperativeHandle,
} from 'react';
import Box from '@material-ui/core/Box';
import { colors, weight } from 'globalStyle';
import { hollowColorType } from 'components/Buttons';

import { useUser } from 'hooks/useUser';
import useUserFunctions from 'hooks/user/useUserFunctions';
import { userType, projectSectionsState, projectStateType } from 'types';
import { Grid, IconButton } from '@material-ui/core';
import { formatNumber } from 'util/formatNumber';
import SimulationIcon from 'assets/icons/stats.svg';
import { Text, Bold } from 'components/Texts';
import ArrowDown from 'assets/icons/arrow_dropdown_white.svg';
import ArrowUp from 'assets/icons/arrow_dropdown_up_white.svg';
import styled, { keyframes } from 'styled-components';
import { useLocation } from 'wouter';
import { projectsNotShowSimulator } from 'types/idProject';

import {
  projectTypes,
  colorByProjectType,
  typesProjectType,
} from 'types/projectType';
import { getContrastTextColor } from 'util/contrast';
import { hasDaysLeft } from 'util/calculateDifferenceBetweenDates';
import StyledHollow from 'components/Cards/components/StyledHollow';
import DetailsComponent from './DetailsComponent';

/*
const STATUS_COLORS = Object.freeze({
  COLLECTING: colors.secondary,
  PENDING_PAYMENT: colors.secondary,
});
*/

export const Card = forwardRef(
  (
    {
      color,
      projectInfo,
      statusProject,
      projectName,
      companyName,
      currentAmount = 0,
      onClick = () => {},
      maxAmount,
      isDesktop,
      handlerSimulator,
    },
    ref,
  ) => {
    const { type, isLogin } = useUser();
    const { isVIP } = useUserFunctions();
    const [view, setView] = useState(true);
    const isVisible = (status) => setView(status);
    const [showInfo, setShowInfo] = useState(true);
    const onClickInfoButton = () => setShowInfo((info) => !info);

    useImperativeHandle(ref, () => ({
      isVisible,
    }));

    if (!view) {
      return '';
    }

    return (
      <AnimatedBox
        showInfo={showInfo}
        style={{
          width: '100%',
          bottom: '50px',
          justifyContent: 'center',
          display: !isDesktop && 'flex',
          marginTop: isDesktop ? '-10px' : '40px',
          position: isDesktop ? 'absolute' : 'relative',
        }}
      >
        <Box
          borderRadius="20px"
          bottom="calc(0% - 60px)"
          bgcolor={color || colorByProjectType[projectInfo?.projectType]}
          marginLeft={isDesktop && '8.085%'}
          width={isDesktop ? '83.83%' : '89.74%'}
          paddingTop={isDesktop ? (showInfo ? '23px' : '20px') : '20px'}
          paddingBottom={isDesktop ? (showInfo ? '30px' : '20px') : '20px'}
        >
          <Grid
            container
            spacing={isDesktop ? 2 : 0}
            style={{
              paddingLeft: isDesktop ? '4.28%' : '2.9%',
              paddingRight: isDesktop ? '1.86%' : '2.9%',
            }}
          >
            <Grid
              item
              xs={isDesktop ? 6 : 12}
              style={{ alignSelf: isDesktop && !showInfo && 'center' }}
            >
              <MainInfo
                bgColor={color}
                showInfo={showInfo}
                isDesktop={isDesktop}
                companyName={companyName}
                projectName={projectName}
                statusProject={statusProject}
                currentAmount={currentAmount}
                endDate={projectInfo?.endDate}
                projectType={projectInfo?.projectType}
                currencyIso={projectInfo?.currencyIso}
              />
            </Grid>
            <Grid item xs={isDesktop ? 6 : 12}>
              <CardDetails
                type={type}
                color={color}
                isVIP={isVIP}
                bgColor={color}
                onClick={onClick}
                isLogin={isLogin}
                showInfo={showInfo}
                isDesktop={isDesktop}
                projectInfo={projectInfo}
                currentAmount={currentAmount}
                onSimulationClick={() => {
                  handlerSimulator();
                }}
                disabled={
                  type === userType.COMPANY
                  || currentAmount === maxAmount
                  || projectInfo?.status !== projectStateType.OPEN
                }
              />
            </Grid>
          </Grid>
          {isDesktop && (
            <AnimatedIconButton
              style={{
                position: 'absolute',
                left: 'calc(50% - 36.5px)',
              }}
              showInfo={showInfo}
              onClick={onClickInfoButton}
              color={color || colorByProjectType[projectInfo?.projectType]}
            >
              <Box
                width="33px"
                height="14px"
                component="img"
                src={showInfo ? ArrowDown : ArrowUp}
                alt={showInfo ? 'Ocultar información' : 'Mostrar información'}
              />
            </AnimatedIconButton>
          )}
        </Box>
      </AnimatedBox>
    );
  },
);

const MainInfo = ({
  bgColor,
  isDesktop,
  statusProject,
  projectName,
  currentAmount,
  showInfo,
  companyName,
  projectType,
  currencyIso,
  endDate,
}) => {
  const statusProps = useMemo(() => {
    let color = colors.primary;
    let text = projectSectionsState[statusProject];
    if (statusProject === projectStateType.OPEN) {
      color = colors.success;
      if (!hasDaysLeft(endDate)) {
        color = colors.primary;
        text = 'Proyecto no disponible';
      }
    } else if (statusProject === projectStateType.ADVERTISING_CAMPAIGN) {
      color = colors.tertiary;
    } else if (
      statusProject === projectStateType.CLOSED
      || statusProject === projectStateType.FAIL_CLOSE
    ) {
      color = colors.GRAY_3;
    } else if (statusProject === projectStateType.PENDING_PAYMENT) {
      color = colors.secondary;
    } else if (statusProject === projectStateType.COLLECTING) {
      color = colors.secondary;
    }
    return {
      color,
      text,
    };
  }, [statusProject, endDate]);
  const color = getContrastTextColor(bgColor || colorByProjectType[projectType]);
  return (
    <Grid
      container
      direction="column"
      alignItems={!isDesktop && 'center'}
      style={{
        color: getContrastTextColor(bgColor) || colors.WHITE,
        paddingLeft: !isDesktop && '7.1%',
        paddingRight: !isDesktop && '7.1%',
        fontSize: isDesktop ? '20px' : '14px',
      }}
    >
      {(showInfo || !isDesktop) && (
        <Grid container>
          <CommonText
            bg={colors.WHITE}
            isCenter={!isDesktop}
            mb={isDesktop ? 15 : 13}
            color={statusProps.GRAY_1}
            width={isDesktop && 'fit-content'}
            padding={isDesktop ? '8px 20px' : '6.5px'}
            style={{
              fontStyle: 'italic',
              borderRadius: '25px',
              fontWeight: weight.BOLD,
              background: colors.WHITE,
              marginRight: isDesktop && '10px',
            }}
          >
            {statusProps.text}
          </CommonText>

          <CommonText
            bg={colors.WHITE}
            isCenter={!isDesktop}
            color={colors.GRAY_1}
            mb={isDesktop ? 15 : 13}
            width={isDesktop && 'fit-content'}
            padding={isDesktop ? '8px 20px' : '6.5px'}
            style={{
              fontStyle: 'italic',
              borderRadius: '25px',
              fontWeight: weight.BOLD,
              background: colors.WHITE,
            }}
          >
            {typesProjectType[projectType]}
          </CommonText>
        </Grid>
      )}
      <CommonText color={color} isCenter={!isDesktop}>
        <Bold>{projectName}</Bold>
      </CommonText>
      <CommonText
        color={color}
        mt={9}
        mb={isDesktop && showInfo && 12}
        isCenter={!isDesktop}
      >
        {`Por ${companyName}`}
      </CommonText>
      {showInfo && isDesktop && (
        <CommonText weight={weight.REGULAR} color={color}>
          Recolectado:
          <Bold>{` ${formatNumber(currentAmount, null, currencyIso)}`}</Bold>
        </CommonText>
      )}
    </Grid>
  );
};

const CommonText = ({ children, color, ...props }) => (
  <Text size="inherit" color={color} {...props}>
    {children}
  </Text>
);

const CardDetails = ({
  isDesktop,
  projectInfo,
  onClick,
  disabled,
  onSimulationClick,
  showInfo,
  isLogin,
  type,
  isVIP,
  color,
}) => {
  const [location] = useLocation();
  const previewPath = 'previsualizacion-proyecto';

  return (
    <Grid container style={{ fontSize: '15px', color: colors.WHITE }}>
      {showInfo && <DetailsComponent project={projectInfo} color={color} />}
      <Box
        width="100%"
        display={
          (
            projectInfo.status === projectStateType.OPEN
            || projectInfo.status === projectStateType.ADVERTISING_CAMPAIGN
          ) ? 'flex' : 'none'
        }
        justifyContent="center"
        marginTop={showInfo && '30px'}
      >
        {isDesktop && projectInfo?.status === projectStateType.WAITING_LEGAL_REP_CONFIRMATION && (
        <StyledHollow
          actived
          isSubmit
          onClick={onClick}
          disabled
          textColor={
            getContrastTextColor(color)
              || getContrastTextColor(
                colorByProjectType[projectInfo?.projectType],
                { defaultDark: colorByProjectType[projectInfo?.projectType] },
              )
          }
          hoverColorText={
            getContrastTextColor(color)
              || getContrastTextColor(
                colorByProjectType[projectInfo?.projectType],
                { defaultDark: colorByProjectType[projectInfo?.projectType] },
              )
          }
          borderColor={!color && colors.WHITE}
          customColor={colors.WHITE}
          style={{
            minWidth: isDesktop && '220px',
            fontSize: isDesktop ? '14px' : '12px',
            marginRight: isDesktop ? '20px' : '10px',
            width: isDesktop ? '41%' : 'calc(50% - 5px)',
          }}
        >
          Invertir
        </StyledHollow>
        )}
        {isDesktop && !location.includes(previewPath) && (
        <>
          {(!isLogin || type === userType.USER) && !isVIP && (
          <StyledHollow
            actived
            isSubmit
            onClick={onClick}
            disabled={
                disabled
                || projectInfo?.status !== projectStateType.OPEN
                || !hasDaysLeft(projectInfo?.endDate)
              }
            hoverColorText={
              getContrastTextColor(
                color || colorByProjectType[projectInfo?.projectType],
                { defaultDark: color || colorByProjectType[projectInfo?.projectType] },
              )
            }
            textColor={
              getContrastTextColor(
                color || colorByProjectType[projectInfo?.projectType],
                { defaultDark: color || colorByProjectType[projectInfo?.projectType] },
              )
            }
            borderColor={!color && colors.WHITE}
            customColor={colors.WHITE}
            style={{
              minWidth: isDesktop && '220px',
              fontSize: isDesktop ? '14px' : '12px',
              marginRight: isDesktop ? '20px' : '10px',
              width: isDesktop ? '41%' : 'calc(50% - 5px)',
            }}
          >
            Invertir
          </StyledHollow>
          )}
          {!projectsNotShowSimulator?.includes(projectInfo?.projectId)
              && projectInfo?.projectType === projectTypes.LOAN
              && (hasDaysLeft(projectInfo?.endDate)
                || projectInfo.status === projectStateType.ADVERTISING_CAMPAIGN) && (
                <StyledHollow
                  isSubmit
                  size="14px"
                  customColor={color}
                  hoverColorText={color}
                  textColor={color && getContrastTextColor(color)}
                  borderColor={color && getContrastTextColor(color)}
                  onClick={onSimulationClick}
                  onColorType={
                    projectInfo?.projectType === projectTypes.LOAN
                      ? hollowColorType.primaryBorder
                      : hollowColorType.tertiaryBorder
                  }
                  style={{
                    minWidth: isDesktop && '220px',
                    fontSize: isDesktop ? '14px' : '12px',
                    width: isDesktop
                      ? projectInfo?.status === projectStateType.OPEN
                        ? '41%'
                        : '50%'
                      : projectInfo?.status === projectStateType.OPEN
                        ? 'calc(50% - 5px)'
                        : 'calc(100% - 20px)',
                  }}
                >
                  {isDesktop && (!color || getContrastTextColor(color) === colors.WHITE) && (
                    <Box
                      alt=""
                      width="17px"
                      component="img"
                      marginRight="10px"
                      src={SimulationIcon}
                      style={{
                        verticalAlign: 'middle',
                      }}
                    />
                  )}
                  Simular inversión
                </StyledHollow>
          )}

        </>
        )}
      </Box>
    </Grid>
  );
};

const fade1 = keyframes`
  0% {
    transform: scale(1, 0.5);
  }
  100% {
    transform: scale(1);
  }
`;

const fade2 = keyframes`
  0% {
    transform: scale(1, 2);
  }
  100% {
    transform: scale(1);
  }
`;

const AnimatedIconButton = styled(IconButton)`
  top: -22px;
  overflow: hidden;
  padding: 20px 20px;
  background-color: ${({ color }) => color} !important;
  animation-fill-mode: forwards;
`;

const AnimatedBox = styled(Box)`
  animation: ${(props) => (props.showInfo ? fade1 : fade2)} 0.2s ease-in;
  animation-fill-mode: forwards;
  transform-origin: bottom;
`;
